import { computed, Injectable, Signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { CustomCookieServiceService } from './custom-cookie-service.service';
import { PopupService } from './popup.service';
import { TutorScreeningService } from './tutor-screening.service';
import { UtilityService } from './utility.service';
import { ScreeningDocType } from '../microservice-clients/tutor-screening.model';
import { UserObj, UserStateEnum } from '../microservice-clients/user';
import { ScreeningUploadDialogComponent } from '../standalone-components/screening-upload-dialog/screening-upload-dialog.component';

/**
 * Führungszeugnis (certificate) reminder for DE tutors who went live before screening existed
 * (SCRUM-797). Onboarding tutors are gated in TutorInitService.passScreeningGate; this covers the
 * live states, for which user-service accepts the certificate only (SCRUM-796). The weekly cadence
 * is frontend-only (cookie): the backend has no reminder or dismiss state.
 */
@Injectable({ providedIn: 'root' })
export class TutorCertificateReminderService {

  private static readonly LIVE_STATES: ReadonlyArray<string> = [
    UserStateEnum.Active,
    UserStateEnum.Chatting,
    UserStateEnum.Introducing,
    UserStateEnum.Tutoring,
  ];
  private static readonly POPUP_INTERVAL_DAYS = 7;

  /**
   * True while the tutor still has to upload (or re-upload) the certificate. Drives the dashboard
   * section. Derived from the user details signal, so a GetUser or the upload's PUT response
   * updates it without any manual refresh.
   */
  readonly uploadRequired: Signal<boolean> = computed(() => this.isUploadRequired(this.authService.userState()));

  private dialogRef: MatDialogRef<ScreeningUploadDialogComponent> | null = null;

  constructor(
    private authService: AuthService,
    private utilityService: UtilityService,
    private screeningService: TutorScreeningService,
    private popupService: PopupService,
    private cookieService: CustomCookieServiceService,
    private matDialog: MatDialog,
  ) { }

  async promptIfDue(): Promise<void> {
    if (!this.isEligibleTutor(this.authService.userDetails) || this.cookieService.hasKey(this.popupCookieName())) {
      return;
    }
    try {
      await firstValueFrom(this.authService.getUserDetails());
    } catch {
    }

    if (!this.uploadRequired() || this.matDialog.openDialogs.length) {
      return;
    }

    this.markPopupShown();
    this.openDialog();
  }

  openDialog(): MatDialogRef<ScreeningUploadDialogComponent> {
    if (this.dialogRef) {
      return this.dialogRef;
    }

    this.dialogRef = this.popupService.openResponsiveDialog(ScreeningUploadDialogComponent);
    const dialog = this.dialogRef.componentInstance;
    dialog.docTypes = [ScreeningDocType.Certificate];
    dialog.reminder = true;
    // The PUT response does not carry tutorDetails.screening; refetch so uploadRequired flips and
    // the dashboard section hides (or stays if the backend did not store the document).
    dialog.onConfirm.subscribe(() => this.authService.getUserDetails().subscribe());
    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
    });

    return this.dialogRef;
  }

  private isUploadRequired(user: UserObj | null | undefined): boolean {
    return this.isEligibleTutor(user)
      && this.screeningService.certificateRequiresUpload(user?.tutorDetails?.screening);
  }

  private isEligibleTutor(user: UserObj | null | undefined): boolean {
    return this.utilityService.isDE
      && this.authService.isTeacher()
      && TutorCertificateReminderService.LIVE_STATES.includes(user?.state ?? '');
  }

  private markPopupShown(): void {
    this.cookieService.put(this.popupCookieName(), 'shown', {
      expires: TutorCertificateReminderService.POPUP_INTERVAL_DAYS,
      path: '/',
    });
  }

  private popupCookieName(): string {
    return `tutor_certificate_reminder_shown_${this.authService.user?.id}`;
  }
}
